import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

const validationSchema = yup.object().shape({
  username: yup
    .string()
    .min(2, "Username must be at least 2 characters.")
    .required(),
  projectName: yup.string().required("Please select a project."),
  scaffoldId: yup.string().nullable(),
  date: yup.string().required("Please select a date."),
  startTime: yup.string().required("Please select a start time."),
  endTime: yup.string().required("Please select an end time."),
  hoursWorked: yup.number().min(0),
  breakTime: yup.number().min(0).default(0),
  workType: yup.string().required("Please select a work type."),
  additionalCompensation: yup.string().nullable(),
  comments: yup.string().nullable(),
});

export default function UserTimeLogForm() {
  const projects = [
    { id: "1", name: "Project Alpha" },
    { id: "2", name: "Project Beta" },
    { id: "3", name: "Project Gamma" },
  ];

  const scaffolds = [
    { id: "s1", name: "Scaffold A-1", projectId: "1" },
    { id: "s2", name: "Scaffold A-2", projectId: "1" },
    { id: "s3", name: "Scaffold B-1", projectId: "2" },
    { id: "s4", name: "Scaffold C-1", projectId: "3" },
  ];

  const workTypes = [
    { id: "1", name: "Regular" },
    { id: "2", name: "Overtime" },
    { id: "3", name: "Night Shift" },
    { id: "4", name: "Weekend" },
  ];

  const compensations = [
    { id: "1", name: "Dust Work Bonus" },
    { id: "2", name: "Chemical Work Bonus" },
    { id: "3", name: "Height Work Bonus" },
    { id: "4", name: "Confined Space Bonus" },
  ];

  const [selectedProject, setSelectedProject] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      username: "John Doe",
      breakTime: 30,
      hoursWorked: 0,
    },
  });

  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const breakTime = watch("breakTime", 0);

  useEffect(() => {
    if (startTime && endTime) {
      
      const startDate = new Date(`2000-01-01T${startTime}:00`);
      const endDate = new Date(`2000-01-01T${endTime}:00`);

      let timeDiffInMs = endDate - startDate;
      if (timeDiffInMs < 0) {
        
        timeDiffInMs += 24 * 60 * 60 * 1000;
      }

      const breakTimeInHours = (breakTime || 0) / 60;
      const hoursWorked = Math.max(
        0,
        timeDiffInMs / (1000 * 60 * 60) - breakTimeInHours
      );

      setValue("hoursWorked", Math.round(hoursWorked * 100) / 100);
    }
  }, [startTime, endTime, breakTime, setValue]);

  function onSubmit(values) {
    alert("Time log submitted successfully!");
    reset();
  }

  return (
    <Card sx={{ maxWidth: "full", mx: "auto", width: "100%" }}>
      <CardHeader
        title={
          <Typography variant="h5" component="h1">
            Log Your Work Hours
          </Typography>
        }
      />
      <CardContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    error={!!errors.username}
                    helperText={errors.username?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="projectName"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.projectName}>
                    <InputLabel id="project-select-label">
                      Project Name
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="project-select-label"
                      label="Project Name"
                      onChange={(e) => {
                        field.onChange(e);
                        setSelectedProject(e.target.value);
                      }}
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.projectName && (
                      <FormHelperText>
                        {errors.projectName.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="scaffoldId"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.scaffoldId}
                    disabled={!selectedProject}
                  >
                    <InputLabel id="scaffold-select-label">
                      Scaffold ID (Optional)
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="scaffold-select-label"
                      label="Scaffold ID (Optional)"
                      value={field.value || ""}
                    >
                      {scaffolds
                        .filter(
                          (scaffold) => scaffold.projectId === selectedProject
                        )
                        .map((scaffold) => (
                          <MenuItem key={scaffold.id} value={scaffold.id}>
                            {scaffold.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {errors.scaffoldId?.message ||
                        "Select a project first to see available scaffolds"}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.date}
                    helperText={errors.date?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Start Time"
                    type="time"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.startTime}
                    helperText={errors.startTime?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="End Time"
                    type="time"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.endTime}
                    helperText={errors.endTime?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="hoursWorked"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Hours Worked"
                    type="number"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.hoursWorked}
                    helperText={
                      errors.hoursWorked?.message || "Automatically calculated"
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="workType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.workType}>
                    <InputLabel id="work-type-select-label">
                      Work Type
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="work-type-select-label"
                      label="Work Type"
                      value={field.value || ""}
                    >
                      {workTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.workType && (
                      <FormHelperText>{errors.workType.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="additionalCompensation"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.additionalCompensation}
                  >
                    <InputLabel id="compensation-select-label">
                      Additional Compensation (Optional)
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="compensation-select-label"
                      label="Additional Compensation (Optional)"
                      value={field.value || ""}
                    >
                      {compensations.map((comp) => (
                        <MenuItem key={comp.id} value={comp.id}>
                          {comp.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.additionalCompensation && (
                      <FormHelperText>
                        {errors.additionalCompensation.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Comments (Optional)"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Add comments here..."
                    value={field.value || ""}
                    error={!!errors.comments}
                    helperText={errors.comments?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                Submit Time Log
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}
